import React, { useState, useEffect, useContext, useMemo, useRef, useLayoutEffect } from "react";
import SideBar from "../../../components/common/SideBar/SideBarNew";
import ParticipantProfileTabs from "../../../components/ServiceProvider/Participant/ParticipantProfileTabs/ParticipantProfileTabs";
import { useLocation, useParams } from "react-router-dom";
import CircularLoader from "../../../components/common/CircularLoader/CircularLoader";
import { LoginContext } from "../../../helpers/LoginContext";
import { apiGetRequest } from "../../../helpers/Requests";
import {
  PARTICIPANT_PROFILE_TABS,
  USER_ROLE,
} from "../../../Constants/constant";
import ComplianceProgressTooltip from "../../../components/common/ProgressBarNew/ComplianceProgressTooltip";
import { roleAccess } from "../../../helpers/RolesPermission";
import { parseBooleanVal, selectedTabState } from "../../../Utils/utils";
import ParticipantProfileHeader from "components/ServiceProvider/Participant/ParticipantProfileHeader/ParticipantProfileHeader";
import {MdErrorOutline} from "react-icons/md";
import useCareLabels from "hooks/useCareLabels";

const ParticipantProfile = (props) => {
  const { participantLabel } = useCareLabels();
  const {
    getFolderListForHRMLoading,
    FolderListForHRM,
    FileListForHRM,
    isLoading,
    getParticipant,
    selectedParticipant,
    resetSelectedParticipant,
    participantOnBoarding,
    participantComplianceItems,
    resetSelectedParticipantDocuments,
    resetParticipantFundings,
    storeCurrentOpenTab,
    item,
  } = props;

  const { sideBarPadding, refreshNavigations } = useContext(LoginContext);
  const { uuid, slug } = useParams();
  const profileMainRef = useRef(null);
  const profileHeaderRef = useRef(null);
  const location = useLocation();

  const { state } = location;
  const moduleType = location.state?.moduleType;
  const [selectedTab, setSelectedTab] = useState(0);
  const [onBoardingProcess, setOnBoardingProcess] = useState([]);
  const [addCaseNoteCall, setAddCaseNoteCall] = useState(false);
  const [request, setRequest] = useState({
    isRequested: false,
    isSuccess: false,
    isFailed: false,
  });
  const [toggleState, setToggleState] = useState(null);

  useEffect(() => {
    if (uuid) {
      if (roleAccess([USER_ROLE.Sp_Standard_User]) && uuid) {
        fetchParticipantDataForWorker();
      }
    }
    setToggleState(
      selectedTabState.get()
        ? { ...selectedTabState.get() }
        : PARTICIPANT_PROFILE_TABS[0]
    );

    return () => {
      resetSelectedParticipant();
      resetParticipantFundings();
      resetSelectedParticipantDocuments();
      sessionStorage.clear();
    };
  }, [uuid, location.pathname]);

  useEffect(() => {
    if (state && state.hasOwnProperty("selectedTab") && state.selectedTab) {
      selectedTabState.save(JSON.stringify(state.selectedTab));
      storeCurrentOpenTab(state.selectedTab);
      setSelectedTab(state.selectedTab.id);
    }
  }, [state]);

  useEffect(() => {
    if (selectedParticipant === null) {
      getParticipant({ participantUUID: uuid });
    } else if (
      selectedParticipant &&
      selectedParticipant.uuid !== uuid &&
      !roleAccess([USER_ROLE.Sp_Standard_User])
    ) {
      getParticipant({ participantUUID: uuid });
    }
  }, [selectedParticipant]);

  useEffect(() => {
    if (toggleState && toggleState.hasOwnProperty("isOpen")) {
      selectedTabState.save(JSON.stringify(toggleState));
    }
    if (toggleState) {
      storeCurrentOpenTab(toggleState);
      setSelectedTab(toggleState.id);
    }
  }, [toggleState]);

  useEffect(() => {
    if (addCaseNoteCall && toggleState.id !== 16) {
      setSelectedTab(16);
      setToggleState(PARTICIPANT_PROFILE_TABS[1]);
    } else {
      setAddCaseNoteCall(false);
    }
  }, [addCaseNoteCall]);

  useLayoutEffect(() => {
    const header = profileHeaderRef.current;
    const main = profileMainRef.current;
    if (!header || !main) return undefined;
    const syncHeaderOffset = () => {
      main.style.setProperty("--profile-header-h", `${header.offsetHeight}px`);
    };
    syncHeaderOffset();
    const observer = new ResizeObserver(syncHeaderOffset);
    observer.observe(header);
    return () => observer.disconnect();
  }, [selectedParticipant, isLoading, toggleState]);

  const complianceRefetched = useRef(false);
  useEffect(() => {
    complianceRefetched.current = false;
  }, [uuid]);

  useEffect(() => {
    if (complianceRefetched.current || !uuid || !getParticipant) return;
    if (
      selectedParticipant?.uuid === uuid &&
      (!participantComplianceItems || participantComplianceItems.length === 0)
    ) {
      complianceRefetched.current = true;
      getParticipant({ participantUUID: uuid });
    }
  }, [
    uuid,
    getParticipant,
    selectedParticipant?.uuid,
    selectedParticipant?.participant_compliance_profile_uuid,
    participantComplianceItems,
  ]);

  const fetchParticipantDataForWorker = async () => {
    setRequest({ ...request, isRequested: true });
    try {
      let res = await apiGetRequest(`hrm/participants/${uuid}`);
      if (res.status !== 200) {
        setRequest({ ...request, isRequested: false, isFailed: true });
        console.log("Something Went Wrong");
      } else if (res.status === 200) {
        setRequest({ ...request, isRequested: false, isSuccess: true });
        setOnBoardingProcess(res?.data?.data?.on_boarding_progress);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const progressBarDetails = useMemo(() => {
    const preUrl = roleAccess([USER_ROLE.Sp_Standard_User])
      ? "participant-profile"
      : "sp-profile";

    const mapComplianceItem = (item) => ({
      user: "participant",
      submitted: item.submitted === true,
      name: item.name || item.form_title,
      type: item.type || "form",
      slug: item.slug,
      cat_slug: item.cat_slug,
      parentSlug: item.slug,
      uuid: item.uuid,
      pre_url: preUrl,
      is_compliance: item.is_compliance,
      compliance_status: item.compliance_status || item.status,
      score: Number(item.score) || 0,
    });

    if (participantComplianceItems?.length > 0) {
      return participantComplianceItems.map(mapComplianceItem);
    }

    const legacySource = roleAccess([USER_ROLE.Sp_Standard_User])
      ? onBoardingProcess
      : participantOnBoarding;

    return (legacySource || []).map((x) => ({
      user: "participant",
      submitted: parseBooleanVal(x.submitted ?? x.status),
      name: x.form_title || x.name,
      type: x.type || "form",
      slug: x.slug,
      cat_slug: x.cat_slug,
      parentSlug: x.slug,
      uuid: x.uuid,
      pre_url: preUrl,
      is_compliance: x.is_compliance,
      compliance_status: x.compliance_status,
      score: Number(x.score) || 0,
    }));
  }, [
    participantComplianceItems,
    participantOnBoarding,
    onBoardingProcess,
  ]);

  const complianceProgressScore = useMemo(() => {
    const items = progressBarDetails || [];

    const calculated = items
      .filter((item) => parseBooleanVal(item.is_compliance))
      .filter((item) => {
        if (item.type === "form") {
          return item.slug !== null && item.slug !== undefined;
        }
        return true;
      })
      .reduce((sum, item) => {
        return item.submitted ? sum + (Number(item.score) || 0) : sum;
      }, 0);

    const storedProgress = Number(selectedParticipant?.on_boarding_progress) || 0;

    if (calculated > 0) {
      return calculated;
    }

    return storedProgress;
  }, [progressBarDetails, selectedParticipant?.on_boarding_progress]);

  return (
    <>
      <div className='min-h-full overflow-x-[clip]'>
        <SideBar />
        <div
          className={`${
            sideBarPadding ? "lg:pl-[4rem]" : "lg:pl-64"
          } flex flex-col duration-300`}
        >
          {toggleState &&
          selectedParticipant &&
          Object.keys(selectedParticipant).length > 0 &&
          isLoading === false ? (
            <main ref={profileMainRef} className='flex-1 w-full'>
              <div ref={profileHeaderRef} className='sticky top-0 z-[20] bg-white'>
                <ParticipantProfileHeader
                  handleAddCaseNote={() => {
                    setAddCaseNoteCall(true);
                  }}
                  selectedTab={selectedTab}
                  activeTab={toggleState}
                  participantInfo={selectedParticipant}
                />
                <div className='max-w-full mx-auto print:hidden'>
                  <div className='px-6 bg-white flex justify-center print:hidden'>
                    {selectedParticipant && (
                      <ComplianceProgressTooltip
                        progressBarDetails={progressBarDetails}
                        uuid={uuid}
                        profileContext='participant'
                        progressline={{
                          on_boarding_progress: complianceProgressScore,
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className='max-w-full mx-auto'>
                <ParticipantProfileTabs
                  refreshNavigations={refreshNavigations}
                  getFolderListForHRMLoading={getFolderListForHRMLoading}
                  FolderListForHRM={FolderListForHRM}
                  FileListForHRM={FileListForHRM}
                  setToggleState={setToggleState}
                  toggleState={toggleState}
                  resetCaseNoteCall={() => setAddCaseNoteCall(false)}
                  isCaseNoteCall={addCaseNoteCall}
                  participantInfo={selectedParticipant}
                  moduleType={moduleType}
                />
              </div>
            </main>
          ) : (
              <div className='px-0 flex justify-center items-center min-h-screen bg-gray-50'>
                {isLoading === true ? (
                    <CircularLoader
                        classes='flex justify-center items-center'
                        classloader='loader-l'
                    />
                  ) : (
                    <div
                        className="flex items-center bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-md shadow-sm">
                      <MdErrorOutline className="text-2xl mr-3 text-red-500"/>
                      <span className="font-semibold">{participantLabel} not found</span>
                    </div>
                )}
              </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ParticipantProfile;
